import { ArrowLeft, LogIn } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Field } from "../components/Field.js";
import { Action, LinkAction, Screen } from "../components/Screen.js";
import { requestPasswordReset } from "../services/authService.js";

/**
 * Ask for a password-reset link.
 *
 * One outcome, always. ADR 0010: this route answers identically whether or not
 * the address has an account, and it is deliberately **not** rate-limited
 * *visibly* — being over the limit looks exactly like success, because a 429
 * here would confirm that somebody else recently asked for mail to this
 * address. So the screen cannot report failure, and does not try: it says what
 * will be true if the address exists, and stops.
 */
export function ForgotPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (sent) {
    return (
      <Screen
        title={t("forgot.sent.title")}
        lead={t("forgot.sent.lead", { email: email.trim() })}
        footer={
          <LinkAction to="/login" icon={LogIn} accent>
            {t("common.backToSignIn")}
          </LinkAction>
        }
      >
        {null}
      </Screen>
    );
  }

  return (
    <Screen
      title={t("forgot.title")}
      lead={t("forgot.lead")}
      footer={
        <LinkAction to="/login" icon={ArrowLeft}>
          {t("common.backToSignIn")}
        </LinkAction>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          // The outcome is not read on purpose — see the note above.
          void requestPasswordReset(email.trim()).finally(() => {
            setSent(true);
          });
        }}
        className="flex flex-col gap-4"
      >
        <Field
          id="email"
          label={t("common.email")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          required
        />
        <Action disabled={busy || !email.trim()}>{t("forgot.action")}</Action>
      </form>
    </Screen>
  );
}
